import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Tab, Tabs, Typography, makeStyles, Grid, Button, IconButton, Icon, CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import HorizontalDivider from 'components/HorizontalDivider';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  format,
  formatISO9075,
  addMonths,
  addYears,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { get } from 'lodash';
import * as AppActions from 'app/store/actions';
import { useDataLoader } from 'app/hooks';
import { useLocation } from 'react-router';
import { parseError } from 'helpers/Error';
import PdfView from 'components/PdfView';
import { SCHEDULE_TAB, SCHEDULE_TAB_TYPE as SCHEDULE_ITEM_TYPES, DASHBOARD_CARD_STYLE } from '../../constants';
import ListItems from './ListItems';
import * as DashboardActions from '../../store/actions';
import AddButton from './assets/images/add_icon.png';

const useStyles = makeStyles(() => ({
  root: {
    minWidth: '72px!important',
    cursor: 'auto',
  },
  shadow: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    ...DASHBOARD_CARD_STYLE,
  },
  headerRow: {
    flexShrink: 0,
  },
  title: {
    fontWeight: 500,
    color: '#0B9FD9',
  },
  tabRoot: {
    minWidth: '72px!important',
    cursor: 'auto',
  },
  contentArea: {
    padding: '0.7rem 16px 16px 16px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  agendaSummary: {
    marginTop: 10,
    marginBottom: 8,
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 10,
    padding: '8px 12px',
    backgroundColor: '#F3FAFD',
    color: '#117CA8',
    fontWeight: 500,
  },
  summaryMainIcon: {
    marginRight: 8,
    color: '#0B9FD9',
  },
  yearSummary: {
    marginTop: 10,
    marginBottom: 8,
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 10,
    padding: '4px 8px',
    backgroundColor: '#F3FAFD',
    color: '#117CA8',
    fontWeight: 500,
  },
  summaryLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  summaryStats: {
    marginTop: 10,
    marginBottom: 8,
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    borderRadius: 10,
    padding: '8px 12px',
    backgroundColor: '#F3FAFD',
    gap: 8,
  },
  summaryStatItem: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    border: '1px solid #BFE3F3',
    backgroundColor: '#FFFFFF',
    color: '#117CA8',
    fontWeight: 500,
    padding: '4px 10px',
  },
  monthNavButton: {
    padding: 4,
  },
  scheduleContent: {
    width: '100%',
    minHeight: 0,
    flex: 1,
    height: '100%',
    overflow: 'hidden',
  },
  emptySchedule: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
}));

const TODAY = new Date();
const CURRENT_YEAR = TODAY.getFullYear();
const YEAR_RANGE = 5;

export const SCHEDULE_TAB_TYPE = {
  [SCHEDULE_TAB.TODAY]: {
    startDate: startOfDay(TODAY),
    endDate: endOfDay(TODAY),
  },
  [SCHEDULE_TAB.WEEK]: {
    startDate: startOfWeek(TODAY, { weekStartsOn: 1 }),
    endDate: endOfWeek(TODAY, { weekStartsOn: 1 }),
  },
  [SCHEDULE_TAB.MONTH]: {
    startDate: startOfMonth(TODAY),
    endDate: endOfMonth(TODAY),
  },
  [SCHEDULE_TAB.YEAR]: {
    startDate: startOfYear(TODAY),
    endDate: endOfYear(TODAY),
  },
};

const MySchedule = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(SCHEDULE_TAB.TODAY);
  const [yearFocusDate, setYearFocusDate] = useState(new Date());
  // const { data } = useSelector((state) => state.dashboard.schedule);
  const { isCreateDone, selectedDate } = useSelector((state) => state.dashboard.calendar);
  const { isEditDone, isDeleted } = useSelector((state) => state.dashboard.schedule);
  const { reportSchedule, reportScheduleLoading } = useSelector((state) => state.dashboard.schedule);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    fetch,
    loading: dataLoading,
    data = [],
    setFilter,
    filter,
  } = useDataLoader({
    url: 'api/schedules',
    abort: true,
    filter: SCHEDULE_TAB_TYPE[SCHEDULE_TAB.TODAY],
    page: 1,
    size: 50,
    lazy: true,
    onLoad: (resp) => ({ data: resp.data, total: resp.total }),
    qs: ({ filter: f }) => {
      const _r = {};
      if (get(f, 'startDate')) _r.startDate = formatISO9075(get(f, 'startDate'));
      if (get(f, 'endDate')) _r.endDate = formatISO9075(get(f, 'endDate'));
      return _r;
    },
    onError: (err) => {
      const { errorMessages, errorTitle } = parseError(err);
      dispatch(AppActions.createDialogMessage('error', errorTitle, errorMessages));
    },
  });

  useEffect(() => {
    if (location.pathname === '/home') {
      setActiveTab(SCHEDULE_TAB.TODAY);
      setYearFocusDate(new Date());
      fetch();
    }
  }, [location]);

  useEffect(() => {
    fetch();
  }, [filter, isCreateDone, isEditDone, isDeleted]);

  useEffect(() => {
    if (activeTab === SCHEDULE_TAB.WEEK) {
      setFilter(SCHEDULE_TAB_TYPE[SCHEDULE_TAB.WEEK]);
    } else if (activeTab === SCHEDULE_TAB.MONTH) {
      setFilter({
        startDate: startOfMonth(new Date(yearFocusDate)),
        endDate: endOfMonth(new Date(yearFocusDate)),
      });
    } else if (activeTab === SCHEDULE_TAB.YEAR) {
      setFilter({
        startDate: startOfYear(new Date(yearFocusDate)),
        endDate: endOfYear(new Date(yearFocusDate)),
      });
    } else if (activeTab === SCHEDULE_TAB.TODAY) {
      setFilter({ startDate: startOfDay(new Date(selectedDate)), endDate: endOfDay(new Date(selectedDate)) });
    }
    fetch();
  }, [activeTab, selectedDate, yearFocusDate]);

  // useEffect(() => {
  //   // TODO Need isDeleted state
  //   // refresh data calendar after add new or delete
  //   if (isCreateDone) {
  //     dispatch(DashboardActions.getCalendarScheduleList(SCHEDULE_TAB_TYPE[SCHEDULE_TAB.YEAR]));
  //   }
  // }, [isCreateDone]);

  const handleTabChange = useCallback((tab) => {
    if (tab === SCHEDULE_TAB.MONTH || tab === SCHEDULE_TAB.YEAR) {
      setYearFocusDate(new Date());
    }
    setActiveTab(tab);
  });
  const handleYearMonthChange = useCallback((monthDiff) => {
    setYearFocusDate((prev) => addMonths(new Date(prev), monthDiff));
  }, []);

  const handleYearChange = useCallback((yearDiff) => {
    setYearFocusDate((prev) => addYears(new Date(prev), yearDiff));
  }, []);

  const handleOpenMonthFromYear = useCallback((monthDate) => {
    setYearFocusDate(new Date(monthDate));
    setActiveTab(SCHEDULE_TAB.MONTH);
  }, []);

  const handlePrintSchedule = () => {
    const focusDate = new Date(yearFocusDate);
    const month = activeTab === SCHEDULE_TAB.MONTH ? focusDate.getMonth() + 1 : undefined;
    const year = activeTab === SCHEDULE_TAB.YEAR ? focusDate.getFullYear() : undefined;
    dispatch(DashboardActions.reportSchedules(activeTab, month, year));
  };

  const handleDeleteSchedule = (id) => {
    dispatch(DashboardActions.deleteSchedule(id));
  };
  const handleEditSchedule = (_data, _id) => {
    dispatch(DashboardActions.editScheduleItem(_data, _id));
  };

  const yearTypeCounts = useMemo(() => {
    const counts = {};
    SCHEDULE_ITEM_TYPES.forEach((type) => {
      counts[type.value] = (data || []).filter((item) => item.type === type.value).length;
    });
    return counts;
  }, [data]);

  const yearSummaryText = format(new Date(yearFocusDate), 'MMMM yyyy');
  const allYearSummaryText = format(new Date(yearFocusDate), 'yyyy');
  const focusYear = new Date(yearFocusDate).getFullYear();
  const isMinYear = focusYear <= CURRENT_YEAR - YEAR_RANGE;
  const isMaxYear = focusYear >= CURRENT_YEAR + YEAR_RANGE;

  return (
    <>
      <Grid container direction="column" className={clsx(classes.shadow)}>
        <Grid item container direction="row" className={clsx('pt-16 pb-6 pl-16', classes.headerRow)}>
          <Grid xs={4} item>
            <Typography variant="h4" className={clsx(classes.title, 'cursor-default')}>
              {t('dashboard:SCHEDULE_TITLE')}
            </Typography>
          </Grid>
          <Grid xs={6} item style={{ paddingTop: '4px' }}>
            <Tabs variant="scrollable" value={activeTab} onChange={(ev, active) => handleTabChange(active)}>
              <Tab
                value={SCHEDULE_TAB.TODAY}
                label={t('dashboard:SCHEDULE_TAB_TODAY')}
                classes={{ root: clsx(classes.tabRoot) }}
              />
              <Tab
                value={SCHEDULE_TAB.WEEK}
                label={t('dashboard:SCHEDULE_TAB_WEEK')}
                classes={{ root: clsx(classes.tabRoot) }}
              />
              <Tab
                value={SCHEDULE_TAB.MONTH}
                label={t('dashboard:SCHEDULE_TAB_MONTH')}
                classes={{ root: clsx(classes.tabRoot) }}
              />
              <Tab
                value={SCHEDULE_TAB.YEAR}
                label={t('dashboard:SCHEDULE_TAB_YEAR')}
                classes={{ root: clsx(classes.tabRoot) }}
              />
            </Tabs>
          </Grid>
          <Grid xs={2} item style={{ paddingTop: '4px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrintSchedule}
              disabled={reportScheduleLoading}
              loading={reportScheduleLoading.toString()}
            >
              {t('dashboard:BUTTON_PRINT')}
            </Button>
          </Grid>
        </Grid>
        <div className={clsx(classes.contentArea)}>
          <HorizontalDivider />
          {activeTab === SCHEDULE_TAB.MONTH && (
            <Grid container alignItems="center" className={clsx(classes.yearSummary)}>
              <Grid item>
                <IconButton
                  className={clsx(classes.monthNavButton)}
                  onClick={() => handleYearMonthChange(-1)}
                  aria-label={t('dashboard:SCHEDULE_PREVIOUS_MONTH')}
                >
                  <Icon fontSize="small">chevron_left</Icon>
                </IconButton>
              </Grid>
              <Grid item className={clsx(classes.summaryLabel)}>
                <Icon fontSize="small" className={clsx(classes.summaryMainIcon)}>
                  calendar_month
                </Icon>
                <Typography variant="body2" className="cursor-default">
                  {t('dashboard:SCHEDULE_SUMMARY_MONTH', { month: yearSummaryText })}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  className={clsx(classes.monthNavButton)}
                  onClick={() => handleYearMonthChange(1)}
                  aria-label={t('dashboard:SCHEDULE_NEXT_MONTH')}
                >
                  <Icon fontSize="small">chevron_right</Icon>
                </IconButton>
              </Grid>
            </Grid>
          )}
          {activeTab === SCHEDULE_TAB.YEAR && (
            <Grid container alignItems="center" justify="space-between" className={clsx(classes.yearSummary)}>
              <Grid item className={clsx(classes.summaryLabel)}>
                <IconButton
                  className={clsx(classes.monthNavButton)}
                  onClick={() => handleYearChange(-1)}
                  aria-label={t('dashboard:SCHEDULE_PREVIOUS_YEAR')}
                  disabled={isMinYear}
                >
                  <Icon fontSize="small">chevron_left</Icon>
                </IconButton>
                <Icon fontSize="small" className={clsx(classes.summaryMainIcon)}>
                  event
                </Icon>
                <Typography variant="body2" className="cursor-default">
                  {t('dashboard:SCHEDULE_SUMMARY_YEAR', { year: allYearSummaryText })}
                </Typography>
                <IconButton
                  className={clsx(classes.monthNavButton)}
                  onClick={() => handleYearChange(1)}
                  aria-label={t('dashboard:SCHEDULE_NEXT_YEAR')}
                  disabled={isMaxYear}
                >
                  <Icon fontSize="small">chevron_right</Icon>
                </IconButton>
              </Grid>
              <Grid item>
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  {dataLoading ? (
                    <Grid item>
                      <CircularProgress size={16} style={{ color: '#0B9FD9', display: 'block' }} />
                    </Grid>
                  ) : (
                    <>
                      <Grid item>
                        <span className={clsx(classes.summaryStatItem)}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            Total: <span style={{ color: '#0B9FD9' }}>{(data || []).length}</span>
                          </Typography>
                        </span>
                      </Grid>
                      {SCHEDULE_ITEM_TYPES.map((type) => (
                        <Grid item key={type.value}>
                          <span className={clsx(classes.summaryStatItem)}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                              {type.label}: <span style={{ color: '#0B9FD9' }}>{yearTypeCounts[type.value] || 0}</span>
                            </Typography>
                          </span>
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item container className={clsx(classes.scheduleContent)}>
            {data?.length > 0 ||
            activeTab === SCHEDULE_TAB.WEEK ||
            activeTab === SCHEDULE_TAB.MONTH ||
            activeTab === SCHEDULE_TAB.YEAR ? (
              <ListItems
                tab={activeTab}
                data={data}
                focusMonth={yearFocusDate}
                onDeleteSchedule={handleDeleteSchedule}
                handleEditSchedule={handleEditSchedule}
                onMonthClick={handleOpenMonthFromYear}
              />
            ) : (
              <Typography component="div" variant="h5" className={clsx(classes.emptySchedule, 'cursor-default')}>
                {t('dashboard:SCHEDULE_EMPTY_HINT_PREFIX')}&nbsp;
                <img
                  style={{ width: '35px', height: 'auto' }}
                  src={AddButton}
                  alt={t('dashboard:SCHEDULE_EMPTY_ICON_ALT')}
                />
                &nbsp;{t('dashboard:SCHEDULE_EMPTY_HINT_SUFFIX')}
              </Typography>
            )}
          </Grid>
        </div>
      </Grid>
      {reportSchedule && (
        <PdfView
          fileName={reportSchedule.fileName}
          fileUrl={reportSchedule.fileUrl}
          handleClose={() => dispatch(DashboardActions.clearReportSchedule())}
        />
      )}
    </>
  );
};

export default MySchedule;
